import { Controller } from '@hotwired/stimulus';

// A chip's state: clicking it opens a menu of the states to pick one from (closing on
// outside click, Tab or Escape). The menu takes the focus when it opens and moves with the arrow
// keys. Every pick fires `chip-state:change` so a parent can act on it, e.g. moving a
// chip's hidden input.
export default class extends Controller {
  static targets = ['button', 'label', 'input', 'menu', 'item'];

  static values = {
    states: Array,
    index: { type: Number, default: 0 },
  };

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  disconnect() {
    this.closeMenu();
  }

  toggleMenu() {
    if (this.menuTarget.hidden) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  choose(event) {
    this.select(event.params.index);
    this.closeMenu();
    this.buttonTarget.focus();
  }

  select(index) {
    if (index === this.indexValue) return;

    this.indexValue = index;
    const state = this.statesValue[index];
    this.dispatch('change', { detail: { index, value: state.value } });
  }

  indexValueChanged() {
    const state = this.statesValue[this.indexValue];
    if (!state) return;

    this.labelTarget.textContent = state.label;
    if (this.hasInputTarget) this.inputTarget.value = state.value;

    this.itemTargets.forEach((item, index) => {
      item.classList.toggle('ui-menu__item--selected', index === this.indexValue);
    });
  }

  openMenu() {
    this.menuTarget.hidden = false;
    this.buttonTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.handleOutsideClick, true);
    document.addEventListener('keydown', this.handleKeydown);
    (this.itemTargets[this.indexValue] || this.itemTargets[0])?.focus();
  }

  closeMenu() {
    if (this.menuTarget.hidden) return;

    this.menuTarget.hidden = true;
    this.buttonTarget.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.handleOutsideClick, true);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleOutsideClick(event) {
    if (this.element.contains(event.target)) return;
    this.closeMenu();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeMenu();
      this.buttonTarget.focus();
    } else if (event.key === 'Tab') {
      this.closeMenu();
    } else {
      this.moveInMenu(event);
    }
  }

  moveInMenu(event) {
    const items = this.itemTargets;
    const current = items.indexOf(document.activeElement);
    const moves = {
      ArrowDown: Math.min(current + 1, items.length - 1),
      ArrowUp: Math.max(current - 1, 0),
      Home: 0,
      End: items.length - 1
    };
    if (current === -1 || !(event.key in moves)) return;

    event.preventDefault();
    items[moves[event.key]].focus();
  }
}
